import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';

import Card from '@mui/joy/Card';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Table from '@mui/joy/Table';
import Tooltip from '@mui/joy/Tooltip';
import IconButton from '@mui/joy/IconButton';
import Button from '@mui/joy/Button';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';



import useSubscription from 'hooks/useSubscription';
import useUserIdentity from 'hooks/useUserIdentity';
import useWorkKey from 'hooks/useWorkKey';
import useRoles from 'hooks/useRoles';
import { getIcon } from 'utils/misc';

import { SubscriptionType } from 'stores/subscription_store';
import { UserProps } from 'stores/user_store';
import { PaymentProps } from 'stores/payment_store';
import { PriceType } from 'utils/types';

import DateUI from 'components/Date';
import Success from 'components/Success';
import Error from 'components/Error';
import LockedFeature from 'components/LockedFeature';


const SubscriptionOptions = observer(({ subscription, handleSync }: { subscription: SubscriptionType, handleSync: () => void }) => {
    const { fetchPriceList, supportSubscriptionChange, supportSubscriptionCancel } = useSubscription();
    const workKey = useWorkKey();
    const { hasRole } = useRoles();
    const [confirmingCancel, setConfirmingCancel] = useState(false);
    const [changingPlan, setChangingPlan] = useState(false);
    const [priceList, setPriceList] = useState([] as PriceType[]);
    const [selectedPlanId, setSelectedPlanId] = useState(subscription.service_subscription_plan_id);

    useEffect(() => {
        setSelectedPlanId(subscription.service_subscription_plan_id);
    }, [subscription.service_subscription_plan_id]);

    useEffect(() => {
        fetchPriceList()
            .then((result) => {
                setPriceList(result || []);
            })
            .catch(() => {
                setPriceList([]);
            });
    }, [fetchPriceList]);

    const doPlanChange = (subscriptionUuid: string, planId: string) => {
        if(!workKey) {
            Error("No identity loaded.");
            return;
        }
        supportSubscriptionChange(workKey,
            subscriptionUuid,
            planId).then((result) => {
                if(!result) {
                    Error("Unable to change plan");
                    return;
                }
                else {
                console.log("result", result);
            Success("Great. Done!")
            setConfirmingCancel(false);
            setChangingPlan(false);
                    console.log('result', result);
                }

            })
            .finally(() => {
                Success("Syncing subscriptions from stripe.")
                handleSync();
            });

    };

    const doCancel = (subscriptionUuid: string) => {
        if(!workKey) {
            Error("No identity loaded.");
            return;
        }
        supportSubscriptionCancel(workKey,
            subscriptionUuid).then((result) => {
            Success("Great. Done! Refresh the page to see the changeds.")
            setConfirmingCancel(false);
            setChangingPlan(false);
            console.log('result', result);
            }).
            finally(() => {
                Success("Syncing subscriptions from stripe.")
                handleSync();
            });
    };


    if(!hasRole('support')) {
        return <Typography level="body-xs">not yet</Typography>;
    }

    if (confirmingCancel) {
        return (
            <Stack spacing={1}>
                <Typography level="body-xs">Are you sure you want to cancel this subscription?</Typography>
                    <Tooltip arrow title="Clicking this button will cancel the subsription right now.">
                <Button
                    onClick={() => { doCancel(subscription.uuid); }}
                    variant="plain" color="danger" size="sm">Yes, Cancel this Plan</Button>
                        </Tooltip>
                <Button onClick={() => setConfirmingCancel(false)} variant="plain" size="sm">Nevermind</Button>
            </Stack>
        );
    }
    else if (changingPlan) {
        return (
            <Stack spacing={1}>
                <Typography level="body-xs">Which Plan?</Typography>
                <Select
                    onChange={(_e, value) => { if (value) { setSelectedPlanId(value) } }}
                    value={selectedPlanId}>
                    {priceList.map((price: PriceType) => {
                        return (
                            <Option value={price.id}>{price.product.name} (${price.unit_amount / 100})</Option>
                        );
                    })}
                </Select>

                {selectedPlanId != subscription.service_subscription_plan_id &&
                    <Tooltip arrow title="Clicking this button will update the subscription.">
                        <Button
                            onClick={() => { doPlanChange(subscription.uuid, selectedPlanId); }}
                            variant="plain"
                            color="success"
                            size="sm">Change to selected</Button>
                    </Tooltip>
                }
                <Button
                    onClick={() => setChangingPlan(false)}
                    variant="plain"
                    size="sm">Nevermind</Button>
            </Stack>
        );
    }

    return (
        <LockedFeature copy="Plan Options">

            <Stack spacing={1}>
                <Button
                    onClick={() => { setConfirmingCancel(false); setChangingPlan(true); }}
                    variant="plain"
                    size="sm">Change Plan</Button>

                <Button
                    onClick={() => { setConfirmingCancel(true); setChangingPlan(false); }}
                    variant="plain"
                    color="danger"
                    size="sm">Cancel Plan</Button>
            </Stack>
        </LockedFeature>
    );
});



const SupportStripeSubscriptionsList = observer(({ user, onPaymentsSync }: { user: UserProps, onPaymentsSync?: (payments: PaymentProps[]) => void }) => {
    const { fetchListByUserViaSupport, subscriptionSync } = useSubscription();

    const {currentIdentity} = useUserIdentity();
    const [didFetch, setDidFetch] = useState(false);
    const [subscriptions, setSubscriptions] = useState([] as SubscriptionType[]);

    const SyncIcon = getIcon('Replay');

    function fetchSubs() {
        fetchListByUserViaSupport(currentIdentity.uuid, user.key)
            .then((result) => {
                setSubscriptions(result.items as SubscriptionType[]);
            })
            .catch(() => {

            })
        .finally(() => {
            setDidFetch(true);
        });
    }

    useEffect(() => {
        if(didFetch) {
            return;
        }
        setDidFetch(true);
        fetchSubs();
    }, [fetchSubs, fetchListByUserViaSupport, user.key]);

    function handleSubSunc() {
        subscriptionSync(currentIdentity.uuid, user.stripe_customer_id).then(data => {
            // New API returns {subscriptions: [...], payments: [...]}
            if (data && data.subscriptions) {
                setSubscriptions(data.subscriptions);
                // Pass payments to parent component if callback provided
                if (data.payments && data.payments.length > 0) {
                    Success(`Synced ${data.subscriptions.length} subscriptions and ${data.payments.length} payments from Stripe`);
                    if (onPaymentsSync) {
                        onPaymentsSync(data.payments);
                    }
                }
            }
            fetchSubs();
        });
    }


    if(!user.stripe_customer_id) {
        return (
            <Card>
            <Stack>
            <Typography level="title-md" color="danger">
                This user does not have a stripe_customer_id
            </Typography>
                <Typography level="body-xs">Not loading subscriptions</Typography>
            </Stack>
            </Card>
        );
    }

    return (
        <Card>
            <Stack>
                <Typography
                    endDecorator={<Tooltip
                                      title="Sync Subsriptions with stripe">
                                      <IconButton onClick={handleSubSunc}><SyncIcon /></IconButton>
                                  </Tooltip>}
                       level="title-md">Subscriptions (Stripe)</Typography>
                <Stack>
                    { !didFetch &&
                     <Typography level="body-xs">Loading Subscriptions</Typography> }

                    { didFetch && subscriptions.length == 0 &&
                    <Typography level="body-sm">No Subscriptions</Typography> }

                    { subscriptions.length > 0 &&
                        <Table>
                            <thead>
                                <tr>
                                    <th>Updated</th>
                                    <th>Plan</th>
                                    <th>Service</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                    </tr>

                                </thead>
                            {subscriptions.map((sub) =>
                                    <tr key={sub.key}>
                                        <td><DateUI dateString={sub.date_updated} /></td>
                                        <td>{sub.plan_name}</td>
                                        <td>{sub.service}</td>
                                        <td>{sub.service_subscription_status}</td>
                                        <td>
                                            {sub.service_subscription_status == 'active' ?
                                                <SubscriptionOptions
                                                    handleSync={handleSubSunc}
                                                    subscription={sub} />
                                                :
                                                <Typography level="body-xs">n/a</Typography>
                                            }
                                        </td>
                                    </tr>
                            )}

                            </Table>
                            }
                </Stack>


            </Stack>
        </Card>
    );

});
export default SupportStripeSubscriptionsList;
